import React, { useEffect, useState, useCallback } from "react";
import { MoveLeft, Download } from "lucide-react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import Loader from "../Loader";
import host from "../../global";

const GradientWPMCircle = ({ wpm }) => {
  const size = 140;      
  const radius = 63;      
  const stroke = 6;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="absolute top-0 left-0"
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient
            id="typingGradient"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.32%" stopColor="#FF60B6" />
            <stop offset="100.25%" stopColor="#2960FD" />
          </linearGradient>
        </defs>

        {/* Background ring */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
          fill="transparent"
        />

        {/* Gradient ring */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          stroke="url(#typingGradient)"
          strokeWidth={stroke}
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>

      {/* Center text */}
      <div className="absolute top-1/2 left-1/2 text-center -translate-x-1/2 -translate-y-1/2">
        <p className="dark:text-white text-textSecondary text-[28px] font-semibold leading-none">
          {wpm}
        </p>
        <p className="dark:text-[#A8A8A8] text-textSecondary text-[12px] leading-tight mt-1">
          Words/ <br /> Min
        </p>
      </div>
    </div>
  );
};

export default function TypingTestReportFinal({ name, onCloseReport ,theme="light", interviewId = null }) {
  const { interview_id: routeInterviewId } = useParams();
  // Public share-token route: the parent passes the real id (the route param is the token).
  const interview_id = interviewId || routeInterviewId;
  const [typingTestData, setTypingTestData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const accessToken = Cookies.get("accessToken");

    const fetchTypingTestResult = async () => {
      try {
        const response = await axios.get(`${host}/get_typing_test_result`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { interview_id },
        });
        setTypingTestData(response.data || {});
      } catch (error) {
        console.error("Failed to fetch typing test result:", error);
        // toast.error("Failed to load typing test results");
      } finally {
        setLoading(false);
      }
    };

    fetchTypingTestResult();
  }, [interview_id]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );

  if (!typingTestData)
    return (
      <div className="py-6 px-6 text-center text-[#A8A8A8]">
        No typing test data found
      </div>
    );

  const {
    wpm = 0,
    accuracy = 0,
    errors = 0,
    words_typed = 0,
    ai_recommendation = {},
  } = typingTestData;

  return (
    <div className="p-3">
      <div className="rounded-2xl px-6 py-3 border border-[rgba(255,255,255,0.06)] dark:bg-[rgba(255,255,255,0.03)] bg-bgPrimary backdrop-blur-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {/* <button onClick={onCloseReport} aria-label="Go back">
              <MoveLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
            </button> */}
            {/* <h3 className="dark:text-[#C9A3FF] text-textPrimary text-lg font-normal ml-4">
              Typing Test Report
            </h3> */}
          </div>
        </div>

        {/* Main content */}
        <div className="flex gap-10 items-start">
          {/* WPM Circle */}
          <GradientWPMCircle wpm={wpm} />

          {/* Details section */}
          <div className="flex-1">
            {/* Recommendation */}
            <div className="text-sm dark:text-[#C9A3FF] text-textSecondary">
              Recommendation:{" "}
              <span className="font-semibold dark:text-[#CCCCCC] text-textSecondary">
                {ai_recommendation.performance_label || "Not Available"}
              </span>
            </div>

            <p className="dark:text-[#C9B0C7] text-textSecondary text-sm mt-2 max-w-2xl leading-relaxed">
              {ai_recommendation.recommendation || "No recommendation available"}
            </p>

            {/* Divider */}
            <div className="mt-4 border-b dark:border-[rgba(255,255,255,0.09)] border-gray-200" />

            {/* Metrics */}
            <div className="mt-4 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <img src="/ACCURACY.png" alt="Accuracy" className="w-5 h-5" />
                <span className="dark:text-[#A8A8A8] text-textSecondary text-sm">Accuracy:</span>
                <span className="dark:text-white text-textSecondary font-medium">{accuracy}%</span>
              </div>

              <div className="h-6 w-px dark:bg-[rgba(255,255,255,0.12)] bg-gray-200" />

              <div className="flex items-center gap-2">
                <img src="/WORD.png" alt="Words" className="w-5 h-5" />
                <span className="dark:text-[#A8A8A8] text-textSecondary text-sm">Total Words:</span>
                <span className="dark:text-white text-textSecondary font-medium">{words_typed}</span>
              </div>

              <div className="h-6 w-px dark:bg-[rgba(255,255,255,0.12)] bg-gray-200" />

              <div className="flex items-center gap-2">
                <img src="/ERROR.png" alt="Errors" className="w-5 h-5" />
                <span className="dark:text-[#A8A8A8] text-textSecondary text-sm">Total Errors:</span>
                <span className="dark:text-white text-textSecondary font-medium">{errors}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
